/**
 * StorageInventory — Süper admin dosya envanteri + optimize sayfası.
 *
 * Şub 16, 2026 — Sistemdeki tüm yüklü dosyaları (documents / vehicle_documents /
 * vehicle_photos / route_attachments / fuel_report_attachments / mail_attachments /
 * vergunning_codes) tek listede boyuta göre desc gösterir. Her satır için:
 *   - Filename + kaynak (source) + sahip (owner_label)
 *   - Boyut (human-readable) + tür (image/pdf)
 *   - Konum (s3 / mongo / local / orphaned)
 *   - "Optimize Et" butonu → Pillow (image) veya pikepdf (PDF) ile küçültür,
 *     ardından S3'e yazar (aynı key veya yeni key).
 * Ayrıca:
 *   - Arama (filename), tür filtresi, min boyut filtresi.
 *   - Toplu "İlk N Optimize Et" butonu.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Search, HardDrive, RefreshCw, Zap, Cloud, Database, HardDriveDownload, AlertTriangle, Image as ImageIcon, FileText, Loader2, Eye, Upload, ExternalLink, Crop, Undo2, Trash2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import ImageEditor from '../../components/ImageEditor';

const LOCATION_META = {
  s3:       { icon: Cloud,             color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  mongo:    { icon: Database,          color: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  local:    { icon: HardDriveDownload, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  orphaned: { icon: AlertTriangle,     color: 'text-rose-700 bg-rose-50 border-rose-200' },
  unknown:  { icon: HardDrive,         color: 'text-slate-600 bg-slate-100 border-slate-300' },
};

const SOURCE_LABEL = {
  documents: 'Belge',
  vehicle_documents: 'Araç Belgesi',
  vehicle_photos: 'Araç Fotoğrafı',
  vergunning_codes: 'Vergunning',
  route_attachments: 'Rota Eki',
  fuel_report_attachments: 'Yakıt Eki',
  mail_attachments: 'Mail Eki',
};

export default function StorageInventory() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalSize, setTotalSize] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fileType, setFileType] = useState('');
  const [minSizeKb, setMinSizeKb] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {row, url, kind}
  const [editor, setEditor] = useState(null);   // {row, url}
  const [editorSaving, setEditorSaving] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const replaceInputRef = useRef(null);
  const replaceRowRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (fileType) params.set('file_type', fileType);
      if (minSizeKb) params.set('min_size_kb', String(minSizeKb));
      params.set('limit', '500');
      const res = await api.get(`/storage/inventory?${params.toString()}`);
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
      setTotalSize(res.data?.total_size_human || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Envanter yüklenemedi');
    } finally { setLoading(false); }
  }, [search, fileType, minSizeKb]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const optimizeOne = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.post(`/storage/inventory/optimize/${row.source}/${row.id}`);
      const d = res.data || {};
      if (d.changed) {
        toast.success(`${row.filename}: ${_fmt(d.before_size)} → ${_fmt(d.after_size)} (${_pct(d.saved, d.before_size)} kazanç)`);
      } else {
        toast.info(d.message || 'Zaten optimize');
      }
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Optimize başarısız');
    } finally { setBusyId(null); }
  };

  const optimizeBulk = async () => {
    if (!window.confirm('İlk 10 büyük dosya optimize edilecek. Devam edilsin mi?')) return;
    setBulkBusy(true);
    try {
      const res = await api.post('/storage/inventory/optimize-bulk', { top_n: 10, file_type: fileType, min_size_kb: minSizeKb });
      const d = res.data || {};
      toast.success(`${d.processed} dosya işlendi · ${d.total_saved_human} kazanç`);
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toplu optimize başarısız');
    } finally { setBulkBusy(false); }
  };

  // Şub 16, 2026 — Önizle: authenticated download → blob URL modal
  const previewOne = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.get(`/storage/inventory/download/${row.source}/${row.id}?inline=1`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const kind = row.type === 'image' ? 'image' : (row.type === 'pdf' ? 'pdf' : 'other');
      setPreview({ row, url, kind });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Önizleme yüklenemedi');
    } finally { setBusyId(null); }
  };

  const closePreview = () => {
    if (preview?.url) { try { URL.revokeObjectURL(preview.url); } catch (_e) { /* ignore */ } }
    setPreview(null);
  };

  // Şub 16, 2026 — Değiştir: user düzenlediği dosyayı seçer → aynı S3 key'e yazılır
  const triggerReplace = (row) => {
    replaceRowRef.current = row;
    if (replaceInputRef.current) {
      replaceInputRef.current.value = '';
      replaceInputRef.current.click();
    }
  };

  const onReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const row = replaceRowRef.current;
    if (!file || !row) return;
    setReplacingId(row.id);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      const res = await api.post(`/storage/inventory/replace/${row.source}/${row.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      toast.success(
        `Yerleştirildi: ${_fmt(d.before_size)} → ${_fmt(d.after_size)}${d.same_key ? ' (aynı URL korundu)' : ''}`
      );
      await fetchList();
      if (preview?.row?.id === row.id) closePreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Değiştirme başarısız');
    } finally {
      setReplacingId(null);
      replaceRowRef.current = null;
    }
  };

  // Şub 16, 2026 — In-browser image editor (crop / rotate / zoom)
  const openEditor = async (row) => {
    setBusyId(row.id);
    try {
      const res = await api.get(`/storage/inventory/download/${row.source}/${row.id}?inline=1`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setEditor({ row, url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Görsel yüklenemedi');
    } finally { setBusyId(null); }
  };

  const closeEditor = () => {
    if (editor?.url) { try { URL.revokeObjectURL(editor.url); } catch (_e) { /* ignore */ } }
    setEditor(null);
  };

  const onEditorSave = async (blob) => {
    if (!editor?.row || !blob) return;
    setEditorSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, editor.row.filename || 'edited.jpg');
      const res = await api.post(`/storage/inventory/replace/${editor.row.source}/${editor.row.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data || {};
      toast.success(`Düzenlendi: ${_fmt(d.before_size)} → ${_fmt(d.after_size)}${d.same_key ? ' (aynı URL)' : ''}`);
      closeEditor();
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kaydedilemedi');
    } finally { setEditorSaving(false); }
  };

  // Yedek geri yükleme / silme
  const restoreBackup = async (row) => {
    if (!window.confirm(`Yedekten geri yüklensin mi?\nDosya: ${row.filename}\nAktif dosya, ${_fmt(row.backup_size)} boyutundaki yedekle değiştirilecek.`)) return;
    setBusyId(row.id);
    try {
      await api.post(`/storage/inventory/restore-backup/${row.source}/${row.id}`);
      toast.success('Yedekten geri yüklendi');
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Geri yükleme başarısız');
    } finally { setBusyId(null); }
  };

  const deleteBackup = async (row) => {
    if (!window.confirm(`Yedek kalıcı olarak silinsin mi?\nDosya: ${row.filename}\nBu işlem geri alınamaz.`)) return;
    setBusyId(row.id);
    try {
      const res = await api.delete(`/storage/inventory/backup/${row.source}/${row.id}`);
      if (res.data?.removed) toast.success('Yedek silindi'); else toast.info(res.data?.message || 'Yedek yoktu');
      await fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Silinemedi');
    } finally { setBusyId(null); }
  };

  const stats = useMemo(() => {
    const byLoc = { s3: 0, mongo: 0, local: 0, orphaned: 0, unknown: 0 };
    const byType = { image: 0, pdf: 0, other: 0 };
    for (const r of rows) {
      byLoc[r.location] = (byLoc[r.location] || 0) + 1;
      byType[r.type] = (byType[r.type] || 0) + 1;
    }
    return { byLoc, byType };
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="storage-inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HardDrive className="h-6 w-6 text-indigo-600" />
            Dosya Envanteri
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Tüm yüklü dosyalar · Boyuta göre sıralı · Optimize butonu ile küçültüp S3&apos;e senkronize edin
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={fetchList} disabled={loading}
            data-testid="inv-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            size="sm"
            onClick={optimizeBulk}
            disabled={bulkBusy || loading}
            data-testid="inv-bulk-btn"
          >
            {bulkBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            İlk 10&apos;u Optimize Et
          </Button>
        </div>
      </div>

      {/* KPI Kartları */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Toplam Dosya</div>
          <div className="text-2xl font-bold mt-0.5">{total}</div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Toplam Boyut</div>
          <div className="text-2xl font-bold mt-0.5">{totalSize || '—'}</div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">S3 / Mongo</div>
          <div className="text-2xl font-bold mt-0.5">
            <span className="text-emerald-600">{stats.byLoc.s3}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-indigo-600">{stats.byLoc.mongo}</span>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
          <div className="text-[11px] text-slate-500 uppercase tracking-wide">Görsel / PDF</div>
          <div className="text-2xl font-bold mt-0.5">
            <span className="text-purple-600">{stats.byType.image}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-rose-600">{stats.byType.pdf}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Dosya adında ara…"
            className="pl-8"
            data-testid="inv-search-input"
          />
        </div>
        <select
          value={fileType}
          onChange={(e) => setFileType(e.target.value)}
          className="text-sm border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 bg-white dark:bg-slate-800"
          data-testid="inv-type-filter"
        >
          <option value="">Tüm Türler</option>
          <option value="image">Görsel</option>
          <option value="pdf">PDF</option>
        </select>
        <select
          value={minSizeKb}
          onChange={(e) => setMinSizeKb(Number(e.target.value) || 0)}
          className="text-sm border border-slate-200 dark:border-slate-700 rounded px-2 py-1.5 bg-white dark:bg-slate-800"
          data-testid="inv-minsize-filter"
        >
          <option value={0}>Tümü (boyut fark etmez)</option>
          <option value={100}>≥ 100 KB</option>
          <option value={500}>≥ 500 KB</option>
          <option value={1024}>≥ 1 MB</option>
          <option value={5120}>≥ 5 MB</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">Dosya</th>
                <th className="px-3 py-2 text-left">Sahip</th>
                <th className="px-3 py-2 text-left">Kaynak</th>
                <th className="px-3 py-2 text-right">Boyut</th>
                <th className="px-3 py-2 text-center">Konum</th>
                <th className="px-3 py-2 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading && !rows.length && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Yükleniyor…</td></tr>
              )}
              {!loading && !rows.length && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">Kayıt yok</td></tr>
              )}
              {rows.map((r) => {
                const loc = LOCATION_META[r.location] || LOCATION_META.unknown;
                const LocIcon = loc.icon;
                const TypeIcon = r.type === 'image' ? ImageIcon : (r.type === 'pdf' ? FileText : HardDrive);
                return (
                  <tr key={`${r.source}-${r.id}`} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30" data-testid={`inv-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <TypeIcon className={`h-4 w-4 ${r.type === 'image' ? 'text-purple-500' : (r.type === 'pdf' ? 'text-rose-500' : 'text-slate-400')}`} />
                        <span className="font-medium truncate max-w-[280px]" title={r.filename}>{r.filename}</span>
                        {r.has_backup && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] bg-amber-50 text-amber-700 border border-amber-200"
                            title={`Yedek: ${_fmt(r.backup_size)} · ${r.backup_key}`}
                            data-testid={`inv-backup-badge-${r.id}`}
                          >
                            <ShieldCheck className="h-3 w-3" />
                            Yedek
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 truncate max-w-[160px]" title={r.owner_label}>{r.owner_label || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[r.source] || r.source}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold whitespace-nowrap">{r.size_human}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium ${loc.color}`}>
                        <LocIcon className="h-3 w-3" />
                        {r.location}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => previewOne(r)}
                          disabled={busyId === r.id || r.location === 'orphaned'}
                          className="h-7 px-2 text-xs"
                          data-testid={`inv-preview-${r.id}`}
                          title={r.location === 'orphaned' ? 'Dosya kaynak sisteminde bulunmuyor' : 'Önizle / indir'}
                        >
                          {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                        </Button>
                        {r.type === 'image' && r.location !== 'orphaned' && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => openEditor(r)}
                            disabled={busyId === r.id}
                            className="h-7 px-2 text-xs text-indigo-700 hover:bg-indigo-50 border-indigo-200"
                            data-testid={`inv-edit-${r.id}`}
                            title="Kırp / Döndür / Zoom → aynı URL'e yaz"
                          >
                            <Crop className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          size="sm" variant="outline"
                          onClick={() => triggerReplace(r)}
                          disabled={replacingId === r.id}
                          className="h-7 px-2 text-xs"
                          data-testid={`inv-replace-${r.id}`}
                          title="Elden düzenlediğiniz yeni dosyayı yükleyin — aynı URL korunur"
                        >
                          {replacingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => optimizeOne(r)}
                          disabled={!r.optimizable || busyId === r.id || r.location === 'orphaned'}
                          className="h-7 px-2 text-xs"
                          data-testid={`inv-optimize-${r.id}`}
                          title={r.location === 'orphaned' ? 'Dosya kaynak sisteminde bulunmuyor' : (r.optimizable ? 'Otomatik optimize et (yedek alınır)' : 'Bu tür otomatik optimize edilemez — Değiştir ile manuel yükleyin')}
                        >
                          <Zap className="h-3 w-3" />
                        </Button>
                        {r.has_backup && (
                          <>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => restoreBackup(r)}
                              disabled={busyId === r.id}
                              className="h-7 px-2 text-xs text-amber-700 hover:bg-amber-50 border-amber-200"
                              data-testid={`inv-restore-${r.id}`}
                              title={`Yedekten geri yükle (${_fmt(r.backup_size)})`}
                            >
                              <Undo2 className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm" variant="outline"
                              onClick={() => deleteBackup(r)}
                              disabled={busyId === r.id}
                              className="h-7 px-2 text-xs text-rose-700 hover:bg-rose-50 border-rose-200"
                              data-testid={`inv-delete-backup-${r.id}`}
                              title="Yedeği kalıcı olarak sil"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gizli replace input — testid'li de değil, dropzone dışında değişme için */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        className="hidden"
        onChange={onReplaceFile}
        data-testid="inv-replace-input"
      />

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={closePreview} data-testid="inv-preview-modal">
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <div className="min-w-0">
                <div className="font-semibold truncate">{preview.row.filename}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  <span>{preview.row.size_human}</span>
                  <span>·</span>
                  <span className="uppercase">{preview.row.location}</span>
                  {preview.row.storage_key && (
                    <>
                      <span>·</span>
                      <span className="font-mono truncate max-w-[400px]" title={preview.row.storage_key}>{preview.row.storage_key}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={preview.url}
                  download={preview.row.filename}
                  className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid="inv-preview-download-btn"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> İndir
                </a>
                {preview.kind === 'image' && (
                  <Button
                    size="sm"
                    onClick={() => { openEditor(preview.row); closePreview(); }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
                    data-testid="inv-preview-edit-btn"
                  >
                    <Crop className="h-3.5 w-3.5 mr-1" />
                    Düzenle
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => triggerReplace(preview.row)}
                  disabled={replacingId === preview.row.id}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                  data-testid="inv-preview-replace-btn"
                >
                  {replacingId === preview.row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
                  Yeni İçerik Yükle
                </Button>
                <button
                  onClick={closePreview}
                  className="text-slate-400 hover:text-slate-700 text-lg leading-none px-2"
                  aria-label="Kapat"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-950 flex items-center justify-center p-4">
              {preview.kind === 'image' && (
                <img src={preview.url} alt={preview.row.filename} className="max-w-full max-h-[70vh] object-contain rounded shadow" />
              )}
              {preview.kind === 'pdf' && (
                <iframe src={preview.url} title={preview.row.filename} className="w-full h-[75vh] rounded bg-white" />
              )}
              {preview.kind === 'other' && (
                <div className="text-slate-500 text-sm text-center p-8">
                  Bu tür tarayıcıda önizlenemiyor.<br />
                  <a href={preview.url} download={preview.row.filename} className="text-emerald-600 underline">İndir</a> veya yandaki <b>Yeni İçerik Yükle</b> ile değiştirin.
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-[11px] text-slate-500">
              İpucu: Görseli indirin, harici bir editörde düzenleyin (crop/scale), ardından <b>Yeni İçerik Yükle</b> ile geri yükleyin — mevcut S3 URL&apos;i korunur.
            </div>
          </div>
        </div>
      )}

      {/* In-browser image editor */}
      {editor && (
        <ImageEditor
          src={editor.url}
          filename={editor.row.filename}
          onSave={onEditorSave}
          onClose={closeEditor}
          loading={editorSaving}
        />
      )}
    </div>
  );
}

// Helpers
function _fmt(bytes) {
  if (!bytes || bytes <= 0) return '0 B';
  let n = bytes;
  for (const u of ['B','KB','MB','GB']) {
    if (n < 1024) return `${n.toFixed(u==='B'?0:1)} ${u}`;
    n /= 1024;
  }
  return `${n.toFixed(1)} TB`;
}
function _pct(saved, before) {
  if (!before) return '0%';
  return `${Math.round((saved / before) * 100)}%`;
}
